import { ApiGeneralDataResponse, ApiResponse } from "types";
import { ApiTags } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import {
  CreateFinanceEntryRequest,
  CreateFinanceRuleRequest,
  DeleteFinanceEntryRequest,
  DeleteFinanceRuleRequest,
  FinanceEntry,
  FinanceRecategorizeResponse,
  FinanceRule,
  FinanceSummaryRow,
  GetFinanceEntriesRequest,
  GetFinanceSummaryRequest,
  UpdateFinanceEntryCategoryRequest,
  UploadFinanceStatementRequest,
  UploadFinanceStatementResponse,
} from "features/finance/types";

const SUMMARY_TAG = { type: ApiTags.FINANCE, id: "SUMMARY" } as const;
const ENTRIES_TAG = { type: ApiTags.FINANCE, id: "ENTRIES" } as const;
const RULES_TAG = { type: ApiTags.FINANCE, id: "RULES" } as const;
const DATA_TAGS = [SUMMARY_TAG, ENTRIES_TAG];

export const financeService = sharedLib.baseApi.injectEndpoints({
  endpoints: (build) => ({
    getFinanceSummary: build.query<
      FinanceSummaryRow[],
      GetFinanceSummaryRequest
    >({
      query: (args) => ({
        url: "get_finance_summary/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<FinanceSummaryRow[]>>
      ) => response.data.data,
      providesTags: () => [SUMMARY_TAG],
    }),
    getFinanceEntries: build.query<FinanceEntry[], GetFinanceEntriesRequest>({
      query: (args) => ({
        url: "get_finance_entries/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<FinanceEntry[]>>
      ) => response.data.data,
      providesTags: () => [ENTRIES_TAG],
    }),
    uploadFinanceStatement: build.mutation<
      UploadFinanceStatementResponse,
      UploadFinanceStatementRequest
    >({
      query: ({ source, currency, file }) => {
        const body = new FormData();
        body.append("source", String(source));
        body.append("currency", currency);
        body.append("statement_file", file);
        return { url: "upload_finance_statement/", method: "POST", body };
      },
      transformResponse: (
        response: ApiResponse<
          ApiGeneralDataResponse<UploadFinanceStatementResponse>
        >
      ) => response.data.data,
      invalidatesTags: DATA_TAGS,
    }),
    createFinanceEntry: build.mutation<void, CreateFinanceEntryRequest>({
      query: (args) => ({
        url: "create_finance_entry/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: DATA_TAGS,
    }),
    updateFinanceEntryCategory: build.mutation<
      void,
      UpdateFinanceEntryCategoryRequest
    >({
      query: (args) => ({
        url: "update_finance_entry_category/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: DATA_TAGS,
    }),
    deleteFinanceEntry: build.mutation<void, DeleteFinanceEntryRequest>({
      query: (args) => ({
        url: "delete_finance_entry/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: DATA_TAGS,
    }),
    getFinanceRules: build.query<FinanceRule[], void>({
      query: () => ({
        url: "get_finance_rules/",
        method: "POST",
        body: {},
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<FinanceRule[]>>
      ) => response.data.data,
      providesTags: () => [RULES_TAG],
    }),
    createFinanceRule: build.mutation<
      FinanceRecategorizeResponse,
      CreateFinanceRuleRequest
    >({
      query: (args) => ({
        url: "create_finance_rule/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<
          ApiGeneralDataResponse<FinanceRecategorizeResponse>
        >
      ) => response.data.data,
      invalidatesTags: [...DATA_TAGS, RULES_TAG],
    }),
    deleteFinanceRule: build.mutation<
      FinanceRecategorizeResponse,
      DeleteFinanceRuleRequest
    >({
      query: (args) => ({
        url: "delete_finance_rule/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<
          ApiGeneralDataResponse<FinanceRecategorizeResponse>
        >
      ) => response.data.data,
      invalidatesTags: [...DATA_TAGS, RULES_TAG],
    }),
  }),
});

export const {
  useGetFinanceSummaryQuery,
  useGetFinanceEntriesQuery,
  useUploadFinanceStatementMutation,
  useCreateFinanceEntryMutation,
  useUpdateFinanceEntryCategoryMutation,
  useDeleteFinanceEntryMutation,
  useGetFinanceRulesQuery,
  useCreateFinanceRuleMutation,
  useDeleteFinanceRuleMutation,
} = financeService;
